import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Package, Wine, BarChart2, ShoppingCart, Truck, ArrowUpCircle, ArrowDownCircle, RefreshCw, ExternalLink, Clock } from 'lucide-react';
import { inventoryService } from '../../../services/inventoryService';

const MOVEMENT_TYPE_LABELS = {
  sale: { label: 'Vente', color: 'text-red-600', icon: <ArrowDownCircle className="h-3.5 w-3.5" /> },
  replenishment: { label: 'Réapprovisionnement', color: 'text-green-600', icon: <ArrowUpCircle className="h-3.5 w-3.5" /> },
  adjustment: { label: 'Correction manuelle', color: 'text-blue-600', icon: <RefreshCw className="h-3.5 w-3.5" /> },
  loading: { label: 'Chargement camion', color: 'text-orange-600', icon: <Truck className="h-3.5 w-3.5" /> },
  return: { label: 'Retour stock', color: 'text-purple-600', icon: <ArrowUpCircle className="h-3.5 w-3.5" /> },
};

const TABS = [
  { id: 'summary', label: 'Résumé', icon: <Package className="h-4 w-4" /> },
  { id: 'history', label: 'Historique stock', icon: <Clock className="h-4 w-4" /> },
  { id: 'orders', label: 'Commandes clients', icon: <ShoppingCart className="h-4 w-4" /> },
  { id: 'supplier', label: 'Fournisseur', icon: <Truck className="h-4 w-4" /> },
  { id: 'analytics', label: 'Analytiques', icon: <BarChart2 className="h-4 w-4" /> },
];

export default function ItemDetailModal({ item, onClose }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('summary');
  const [movements, setMovements] = useState([]);
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loadingTab, setLoadingTab] = useState(false);

  const stock = item?.stockQuantity || 0;
  const threshold = item?.reorderThreshold || item?.minStockLevel || item?.minStock || 10;
  const salePrice = item?.salePrice || item?.priceEuros || item?.unitPriceSell || 0;
  const purchasePrice = item?.purchasePrice || item?.unitPriceBuy || 0;
  const margin = salePrice > 0 ? (((salePrice - purchasePrice) / salePrice) * 100)?.toFixed(1) : '—';

  const getStockStatus = () => {
    if (stock === 0) return { label: 'Rupture', color: 'bg-red-100 text-red-700' };
    if (stock <= threshold) return { label: 'Stock bas', color: 'bg-yellow-100 text-yellow-700' };
    return { label: 'OK', color: 'bg-green-100 text-green-700' };
  };
  const stockStatus = getStockStatus();

  useEffect(() => {
    if (activeTab === 'history') loadMovements();
    else if (activeTab === 'orders') loadOrders();
    else if (activeTab === 'analytics') loadAnalytics();
  }, [activeTab]);

  const loadMovements = async () => {
    setLoadingTab(true);
    try {
      const data = await inventoryService?.getMovements(item?.id, item?.tableSource);
      setMovements(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingTab(false);
    }
  };

  const loadOrders = async () => {
    setLoadingTab(true);
    try {
      const data = await inventoryService?.getOrdersForItem(item?.id, item?.tableSource);
      setOrders(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingTab(false);
    }
  };

  const loadAnalytics = async () => {
    setLoadingTab(true);
    try {
      const data = await inventoryService?.getAnalyticsForItem(item?.id, item?.tableSource);
      setAnalytics(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingTab(false);
    }
  };

  const handleReplenish = () => {
    navigate(`/unified-replenishment-center?item=${item?.id}&name=${encodeURIComponent(item?.name || '')}&table=${item?.tableSource}`);
  };

  const handleViewSupplier = () => {
    navigate('/carton-suppliers-management');
  };

  const handleViewAnalytics = () => {
    navigate(`/unified-stock-analytics?item=${item?.id}&name=${encodeURIComponent(item?.name || '')}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            {item?.tableSource === 'barrel_inventory'
              ? <Wine className="h-6 w-6 text-amber-600" />
              : <Package className="h-6 w-6 text-blue-600" />}
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{item?.name || item?.barrelType || item?.cartonType}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">{item?.sku ? `SKU: ${item?.sku}` : item?.category}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex overflow-x-auto border-b border-gray-200 dark:border-slate-700 px-4">
          {TABS?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab?.id
                  ? 'border-green-600 text-green-600' :'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {tab?.icon} {tab?.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* TAB: RÉSUMÉ */}
          {activeTab === 'summary' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Stock actuel</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stock}</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Prix vente</p>
                  <p className="text-2xl font-bold text-green-600">{salePrice?.toFixed(2)} €</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Prix achat</p>
                  <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{purchasePrice?.toFixed(2)} €</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Marge</p>
                  <p className="text-2xl font-bold text-blue-600">{margin}%</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Seuil réappro</p>
                  <p className="text-2xl font-bold text-orange-600">{threshold}</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Statut stock</p>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${stockStatus?.color}`}>
                    {stockStatus?.label}
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Catégorie</span>
                  <span className="font-medium text-gray-900 dark:text-white capitalize">{item?.category || '—'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Emplacement</span>
                  <span className="font-medium text-gray-900 dark:text-white">{item?.location || '—'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Fournisseur principal</span>
                  <span className="font-medium text-gray-900 dark:text-white">{item?.supplierName || '—'}</span>
                </div>
                {item?.dimensions && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Dimensions</span>
                    <span className="font-medium text-gray-900 dark:text-white">{item?.dimensions}</span>
                  </div>
                )}
                {item?.description && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Description</span>
                    <span className="font-medium text-gray-900 dark:text-white text-right max-w-xs">{item?.description}</span>
                  </div>
                )}
              </div>
              <button
                onClick={handleReplenish}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 font-medium text-sm"
              >
                <RefreshCw className="h-4 w-4" /> Réapprovisionner cet article
              </button>
            </div>
          )}

          {/* TAB: HISTORIQUE STOCK */}
          {activeTab === 'history' && (
            <div>
              {loadingTab ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">Chargement...</div>
              ) : movements?.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Clock className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>Aucun mouvement de stock enregistré</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {movements?.map(m => {
                    const typeInfo = MOVEMENT_TYPE_LABELS?.[m?.movement_type] || { label: m?.movement_type, color: 'text-gray-600', icon: null };
                    return (
                      <div key={m?.id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                        <span className={typeInfo?.color}>{typeInfo?.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${typeInfo?.color}`}>{typeInfo?.label}</p>
                          {m?.comment && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{m?.comment}</p>}
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-bold ${m?.quantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {m?.quantity > 0 ? '+' : ''}{m?.quantity}
                          </p>
                          <p className="text-xs text-gray-400">{m?.previous_stock} → {m?.new_stock}</p>
                        </div>
                        <p className="text-xs text-gray-400 whitespace-nowrap">
                          {new Date(m?.created_at)?.toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB: COMMANDES CLIENTS */}
          {activeTab === 'orders' && (
            <div>
              {loadingTab ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">Chargement...</div>
              ) : orders?.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <ShoppingCart className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>Aucune commande client liée à cet article</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {orders?.map(o => (
                    <div key={o?.id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{o?.customer_name || o?.client_name || '—'}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{o?.customer_phone || o?.client_phone || ''}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900 dark:text-white">{o?.quantity || 1} unité(s)</p>
                        <p className="text-xs text-gray-500">{o?.total_price ? `${o?.total_price} €` : ''}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        o?.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                        o?.payment_status === 'pending'? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {o?.payment_status || o?.order_status || '—'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={() => navigate('/barrel-customer-orders-management')}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
              >
                <ExternalLink className="h-4 w-4" /> Voir toutes les commandes clients
              </button>
            </div>
          )}

          {/* TAB: FOURNISSEUR */}
          {activeTab === 'supplier' && (
            <div className="space-y-4">
              {item?.supplierName ? (
                <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Truck className="h-5 w-5 text-amber-600" />
                    <p className="font-semibold text-gray-900 dark:text-white">{item?.supplierName}</p>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Fournisseur principal lié à cet article</p>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  <Truck className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>Aucun fournisseur lié à cet article</p>
                  <p className="text-xs mt-1">Modifiez l'article pour ajouter un fournisseur</p>
                </div>
              )}
              <button
                onClick={handleViewSupplier}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-700 font-medium text-sm"
              >
                <ExternalLink className="h-4 w-4" /> Voir fiche fournisseur
              </button>
            </div>
          )}

          {/* TAB: ANALYTIQUES */}
          {activeTab === 'analytics' && (
            <div className="space-y-4">
              {loadingTab ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">Chargement...</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Ventes 7 jours</p>
                      <p className="text-2xl font-bold text-blue-600">{analytics?.sales7Days || 0}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Ventes 30 jours</p>
                      <p className="text-2xl font-bold text-green-600">{analytics?.sales30Days || 0}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total mouvements</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics?.totalMovements || 0}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">CA estimé (30j)</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {((analytics?.sales30Days || 0) * salePrice)?.toFixed(0)} €
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleViewAnalytics}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium text-sm"
                  >
                    <BarChart2 className="h-4 w-4" /> Voir analytiques complètes
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
